const { useCallback, useEffect, useMemo, useRef, useState } = require('react');
const { useDeepCompareEffect } = require('hooks/useDeepCompareEffect');

exports.useChatMessageState = useChatMessageState;

const internals = {};

internals.matches = (pending, message, currentUserId) => {

    if (!message.user?.isMe && message.user?.id !== currentUserId) {
        return false;
    }

    const clientMessageId = message.clientMessageId || message.attributes?.clientMessageId;
    return !!((clientMessageId && clientMessageId === pending.clientMessageId)
        || (typeof pending.messageIndex === 'number' && pending.messageIndex === message.index)
        || (pending.serverId !== undefined && (pending.serverId === message.id || pending.serverId === message.sid)));
};

function useChatMessageState({ currentUserId, sid, messages }) {

    const scope = useMemo(() => ({}), [currentUserId, sid]);
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const optimisticRef = useRef({ scope, messages: [] });
    const [stableState, setStableState] = useState({ scope, messages });
    const deadlinesRef = useRef(new Map());
    const pinnedMessages = useMemo(() => messages.filter(({ isPinned }) => !!isPinned), [messages]);

    const synchronize = useCallback(() => {

        if (optimisticRef.current.scope !== scope) {
            optimisticRef.current = { scope, messages: [] };
        }

        optimisticRef.current.messages = optimisticRef.current.messages.filter((pending) => !messagesRef.current.some((message) => internals.matches(pending, message, currentUserId)));
        setStableState({ scope, messages: messagesRef.current.concat(optimisticRef.current.messages) });
    }, [scope, currentUserId]);

    useDeepCompareEffect(synchronize, [currentUserId, sid, messages]);

    useEffect(() => {

        scopeRef.current = scope;
        const deadlines = deadlinesRef.current;
        return () => {

            if (scopeRef.current === scope) {
                scopeRef.current = null;
            }

            for (const [timer, cancel] of deadlines) {
                clearTimeout(timer);
                cancel();
            }

            deadlines.clear();
        };
    }, [scope]);

    const addOptimisticMessage = useCallback((message) => {

        if (scopeRef.current !== scope) {
            return;
        }

        if (optimisticRef.current.scope !== scope) {
            optimisticRef.current = { scope, messages: [] };
        }

        optimisticRef.current.messages = optimisticRef.current.messages.concat(message);
        synchronize();
    }, [scope, synchronize]);

    const sendOptimisticMessage = useCallback((message, submit) => {

        if (scopeRef.current !== scope) {
            return Promise.resolve('unconfirmed');
        }

        addOptimisticMessage({ ...message, isOptimistic: true, sendStatus: 'sending' });
        return new Promise((resolve) => {

            let settled = false;
            const finish = (sendStatus, acknowledgement = {}) => {

                if (settled) {
                    return;
                }

                settled = true;
                clearTimeout(timer);
                deadlinesRef.current.delete(timer);
                if (scopeRef.current === scope) {
                    optimisticRef.current.messages = optimisticRef.current.messages.map((pending) => pending.clientMessageId === message.clientMessageId
                        ? { ...pending, sendStatus, ...acknowledgement }
                        : pending);
                    synchronize();
                }

                resolve(sendStatus);
            };

            const timer = setTimeout(() => finish('unconfirmed'), 35000);
            deadlinesRef.current.set(timer, () => { settled = true; resolve('unconfirmed'); });
            Promise.resolve().then(() => scopeRef.current === scope ? submit() : undefined).then((action) => {

                if (action?.error) {
                    finish(action.payload?.error?.deliveryUncertain === false ? 'failed' : 'unconfirmed');
                    return;
                }

                const result = action?.payload?.result;
                if (typeof result?.messageIndex === 'number') {
                    finish('sent', { messageIndex: result.messageIndex });
                }
                else if (typeof result?.result === 'string' || typeof result?.result === 'number') {
                    finish('sent', { serverId: result.result });
                }
                else {
                    finish('unconfirmed');
                }
            }, (error) => finish(error?.deliveryUncertain === false ? 'failed' : 'unconfirmed'));
        });
    }, [scope, addOptimisticMessage, synchronize]);

    return {
        addOptimisticMessage,
        sendOptimisticMessage,
        optimisticMessagesLength: optimisticRef.current.scope === scope ? optimisticRef.current.messages.length : 0,
        pinnedMessages,
        stableMessages: stableState.scope === scope ? stableState.messages : messages
    };
}
